import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useAuth } from "../../auth";
import IstioService, { exposureErrorDetail } from "../../services/istio.service";
import {
  BUCKET_HINT,
  BUCKET_LABEL,
  IReconcileEntry,
  IReconcilePlan,
  IReconcileResult,
  ReconcileBucket,
} from "../../types/api/istio.type";

const BUCKET_COLOR: Record<ReconcileBucket, "success" | "error" | "warning" | "info" | "default"> = {
  in_sync: "success",
  missing: "error",
  unrecorded: "warning",
  port_mismatch: "warning",
  name_mismatch: "warning",
  duplicate: "error",
  port_shadowed: "error",
  port_taken: "error",
  no_database_service: "info",
  port_undeclared: "error",
};

/** Buckets adopt can repair: all three are fixed by writing what is live into the record. */
const ADOPTABLE: ReconcileBucket[] = ["unrecorded", "port_mismatch", "name_mismatch"];

const ORDER: ReconcileBucket[] = [
  "missing",
  "unrecorded",
  "port_mismatch",
  "name_mismatch",
  "duplicate",
  "port_shadowed",
  "port_taken",
  "port_undeclared",
  "no_database_service",
  "in_sync",
];

const formatStamp = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

/** "18 minutes", "2 hours", "3 days" — for how long ago something happened. */
const formatAgo = (iso?: string | null): string | null => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 90) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  return `${Math.round(hours / 24)} days ago`;
};

/** True when the newest record post-dates the backup it would be restored from. */
const isAfter = (a?: string | null, b?: string | null): boolean => {
  if (!a || !b) return false;
  const x = new Date(a).getTime();
  const y = new Date(b).getTime();
  return !Number.isNaN(x) && !Number.isNaN(y) && x > y;
};

/** Reads after "in the last …", so a lone hour drops the numeral. */
const formatWindow = (seconds: number) => {
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return hours === 1 ? "hour" : `${hours} hours`;
  }
  return `${Math.round(seconds / 60)} minutes`;
};

const summarise = (result: IReconcileResult, verb: "Rebuilt" | "Recorded") => {
  const done = verb === "Rebuilt" ? result.applied.length : result.adopted.length;
  const parts = [`${verb} ${done} exposure${done === 1 ? "" : "s"}.`];
  if (result.failed.length) {
    parts.push(
      `${result.failed.length} failed: ${result.failed
        .map((f) => `env ${f.env_id} (${f.error || f.code})`)
        .join("; ")}.`
    );
  }
  // Without this, asking for an id that has since moved bucket reads as a silent no-op.
  if (result.skipped?.length) {
    parts.push(`Skipped: ${result.skipped.map((s) => `env ${s.env_id} ${s.reason}`).join("; ")}.`);
  }
  return parts.join(" ");
};

export const IstioReconcile = () => {
  // Recorded by the server for the audit log: these actions span every tenant, so there is
  // no owner to check them against. Without an identity the buttons are disabled rather than
  // attributing the change to a placeholder.
  const { user } = useAuth();
  const actor: string | null = user?.email || user?.name || null;
  const [plan, setPlan] = useState<IReconcilePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | "all" | "adopt" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [confirmAdopt, setConfirmAdopt] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await IstioService.reconcilePlan();
      setPlan(data);
      setError(null);
    } catch (e) {
      setError(exposureErrorDetail(e).message || "Could not compute a reconciliation plan");
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(
    async (key: number | "all" | "adopt", action: () => Promise<{ data: IReconcileResult }>, verb: "Rebuilt" | "Recorded") => {
      setBusy(key);
      setOutcome(null);
      try {
        const { data } = await action();
        setOutcome(summarise(data, verb));
        setError(null);
        await load();
      } catch (e) {
        setError(exposureErrorDetail(e).message || "The action failed");
      } finally {
        setBusy(null);
      }
    },
    [load]
  );

  const grouped = useMemo(() => {
    const byBucket = new Map<ReconcileBucket, IReconcileEntry[]>();
    (plan?.entries ?? []).forEach((entry) => {
      byBucket.set(entry.bucket, [...(byBucket.get(entry.bucket) ?? []), entry]);
    });
    return ORDER.filter((bucket) => (byBucket.get(bucket) ?? []).length > 0).map((bucket) => ({
      bucket,
      entries: byBucket.get(bucket) ?? [],
    }));
  }, [plan]);

  if (loading && !plan) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !plan) {
    return (
      <Box p={2}>
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!plan) return null;

  if (!plan.rbac_ok) {
    return (
      <Box p={2}>
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          Recovery is unavailable: {plan.rbac_error || "Istio objects cannot be read"}
        </Alert>
      </Box>
    );
  }

  const freshness = plan.record_freshness;
  const newest = formatStamp(freshness.newest_exposure_at);
  const backup = formatStamp(freshness.backup_last_success_at);
  // The case that confuses people: an exposure recorded after the last backup is on record
  // but would not come back from it.
  const unbackedUp = isAfter(freshness.newest_exposure_at, freshness.backup_last_success_at);

  return (
    <Box p={2}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {outcome && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setOutcome(null)}>
          {outcome}
        </Alert>
      )}

      {/* Answers one question — "what would I lose if the cluster died right now?" — and
          keeps the two timestamps apart. They come from different sources, and quoting both
          in sequence read as if they described the same thing. */}
      <Alert severity={!newest ? "warning" : unbackedUp ? "warning" : "info"} sx={{ mb: 2 }}>
        {!newest ? (
          <>
            <strong>Nothing is recorded yet</strong> — a cluster loss now could not be rebuilt.
            Use <strong>Record all</strong> below to back-fill from what is live.
          </>
        ) : unbackedUp ? (
          <>
            <strong>The newest exposure is not backed up yet</strong> — a cluster loss now would
            lose it. Recorded {formatAgo(freshness.newest_exposure_at)}; last accounts backup{" "}
            {backup} ({formatAgo(freshness.backup_last_success_at)}), hourly.
          </>
        ) : backup ? (
          <>
            <strong>Everything on record is backed up</strong>, as of {backup} (
            {formatAgo(freshness.backup_last_success_at)}). Anything exposed from now is at risk
            until the next hourly backup.
          </>
        ) : (
          <>
            <strong>
              Anything exposed in the last {formatWindow(freshness.blind_window_seconds)} may not
              survive a cluster loss.
            </strong>{" "}
            The backup time cannot be read
            {freshness.backup_note ? `: ${freshness.backup_note}` : ""}.
          </>
        )}
        <Box component="div" sx={{ mt: 0.5, fontSize: 12, opacity: 0.85 }}>
          {freshness.recorded_count ?? "—"} exposure
          {freshness.recorded_count === 1 ? "" : "s"} on record
          {newest ? ` · newest recorded ${newest}` : ""}
        </Box>
      </Alert>

      {!actor && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Recovery actions are disabled: the server records who ran them, and this session has
          no signed-in identity to attribute them to.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
          {ORDER.map((bucket) => (
            <Tooltip key={bucket} title={BUCKET_HINT[bucket]}>
              <Chip
                size="small"
                color={plan.counts[bucket] ? BUCKET_COLOR[bucket] : "default"}
                variant={plan.counts[bucket] ? "filled" : "outlined"}
                label={`${BUCKET_LABEL[bucket]}: ${plan.counts[bucket] ?? 0}`}
              />
            </Tooltip>
          ))}
          <Box flexGrow={1} />
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || busy !== null}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            disabled={!plan.adoptable || busy !== null || !actor}
            onClick={() => setConfirmAdopt(true)}
          >
            Record all ({plan.adoptable})
          </Button>
          <Tooltip title="Rebuilds every missing exposure on its recorded port and name">
            <span>
              <Button
                variant="contained"
                color="error"
                disabled={!plan.actionable || busy !== null || !actor}
                onClick={() => run("all", () => IstioService.reconcileApply(actor!), "Rebuilt")}
              >
                {busy === "all" ? "Applying…" : `Apply all (${plan.actionable})`}
              </Button>
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {plan.gateway} · reconcile never deletes anything and never moves a live exposure
        </Typography>
      </Paper>

      {plan.unidentified.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {plan.unidentified.length} VirtualService
          {plan.unidentified.length === 1 ? "" : "s"} hold a port but carry no environment id, so
          they can be neither recorded nor rebuilt:{" "}
          {plan.unidentified.map((u) => `${u.virtual_service} (${u.port})`).join(", ")}.
        </Alert>
      )}

      {grouped.length === 0 && <Alert severity="success">Nothing to reconcile.</Alert>}

      {grouped.map(({ bucket, entries }) => (
        <Box key={bucket} mb={2}>
          <Tooltip title={BUCKET_HINT[bucket]}>
            <Typography variant="subtitle2" gutterBottom>
              {BUCKET_LABEL[bucket]} ({entries.length})
            </Typography>
          </Tooltip>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Environment</TableCell>
                  <TableCell>Recorded</TableCell>
                  <TableCell>Live</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.env_id} hover>
                    <TableCell>{entry.env_id}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {entry.recorded_port ? `${entry.recorded_port} · ${entry.recorded_name}` : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {entry.live_port ? `${entry.live_port} · ${entry.live_name}` : "—"}
                      {entry.live_managed === false && " (hand-made)"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{entry.note ?? "—"}</TableCell>
                    <TableCell align="right">
                      {bucket === "missing" && (
                        <Button
                          size="small"
                          disabled={busy !== null || !actor}
                          onClick={() =>
                            run(entry.env_id, () => IstioService.reconcileApply(actor!, [entry.env_id]), "Rebuilt")
                          }
                        >
                          {busy === entry.env_id ? "Applying…" : "Apply"}
                        </Button>
                      )}
                      {ADOPTABLE.includes(bucket) && (
                        <Button
                          size="small"
                          disabled={busy !== null || !actor}
                          onClick={() =>
                            run(entry.env_id, () => IstioService.reconcileAdopt(actor!, [entry.env_id]), "Recorded")
                          }
                        >
                          {busy === entry.env_id ? "Recording…" : "Record"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      <Dialog open={confirmAdopt} onClose={() => setConfirmAdopt(false)}>
        <DialogTitle>Record {plan.adoptable} exposure(s)?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This writes what is live into the environment's record in accounts. No VirtualService is
            created, changed or deleted.
            <Box component="p" sx={{ mt: 2 }}>
              <strong>Ownership changes at recovery.</strong> A hand-made VirtualService recorded
              here is rebuilt by database-manager if the cluster is ever lost, and the rebuilt object
              is then managed by it — so deleting the environment afterwards would also remove the
              VirtualService, where today it is left in place.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAdopt(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmAdopt(false);
              run("adopt", () => IstioService.reconcileAdopt(actor!), "Recorded");
            }}
          >
            Record them
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IstioReconcile;
